import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Activity, 
  LogIn, 
  LogOut, 
  Plus,
  Edit,
  Trash2,
  Shield,
  ToggleLeft
} from 'lucide-react';

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  create: Plus,
  update: Edit,
  delete: Trash2,
  permission_change: Shield,
  feature_toggle: ToggleLeft
};

const ACTION_LABELS = {
  login: 'Anmeldung',
  logout: 'Abmeldung',
  create: 'Erstellt',
  update: 'Aktualisiert',
  delete: 'Gelöscht',
  permission_change: 'Rechte geändert',
  feature_toggle: 'Feature umgeschaltet'
};

const AdminAuditLogs = () => {
  const { theme } = useAuth();
  const [logs, setLogs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFeature, setFilterFeature] = useState('all');

  useEffect(() => {
    fetchData();
  }, [filterFeature]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filterFeature !== 'all' ? `?feature_id=${filterFeature}` : '';
      const [logsRes, featuresRes] = await Promise.all([
        axios.get(`/admin/audit-logs${params}`),
        axios.get('/admin/features')
      ]);
      setLogs(logsRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Audit-Logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const Icon = ACTION_ICONS[action] || Activity;
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-gray-100 text-gray-700',
      create: 'bg-blue-100 text-blue-700',
      update: 'bg-yellow-100 text-yellow-700',
      delete: 'bg-red-100 text-red-700',
      permission_change: 'bg-purple-100 text-purple-700',
      feature_toggle: 'bg-orange-100 text-orange-700'
    };
    
    return (
      <Badge className={colors[action] || 'bg-gray-100 text-gray-700'}>
        <Icon className="w-3 h-3 mr-1" />
        {ACTION_LABELS[action] || action}
      </Badge>
    );
  };

  const cardBg = theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Audit-Logs
          </h1>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Protokoll aller wichtigen Aktionen (DSGVO-konform: ohne IP-Adressen)
          </p>
        </div>
        
        <Select value={filterFeature} onValueChange={setFilterFeature}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Feature filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Features</SelectItem>
            {features.map(feature => (
              <SelectItem key={feature.id} value={feature.id}>
                {feature.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className={cardBg}>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Keine Audit-Logs vorhanden</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{log.user_email}</span>
                    </TableCell>
                    <TableCell>
                      {getActionBadge(log.action)}
                    </TableCell>
                    <TableCell>
                      {log.feature_id ? (
                        <Badge variant="outline">{log.feature_id}</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="text-sm text-gray-500">
                        {log.resource_type && (
                          <span className="mr-2">
                            <Badge variant="secondary" className="text-xs mr-1">
                              {log.resource_type}
                            </Badge>
                          </span>
                        )}
                        {log.details || '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className={cardBg}>
        <CardHeader>
          <CardTitle className="text-lg">Datenschutz-Hinweis</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            Die Audit-Logs erfassen keine IP-Adressen und keine unnötigen 
            personenbezogenen Daten. Sie dienen ausschließlich der 
            Nachvollziehbarkeit von Änderungen im System und erfüllen 
            die Anforderungen der DSGVO.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
